import { css } from '@emotion/react';
import { TextField, Typography } from '@mui/material';
import { useAtomValue } from 'jotai/index';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useState } from 'react';
import { MultiSelect } from '../../../../CoreComponents/base/MultiSelect';
import { SearchInput } from '../../../../CoreComponents/base/SearchInput';
import { UnknownUser } from '../../../../data-models/user.data-model';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { usersByEmailMapAtom } from '../../../../services/state/AppConfigStateJ';
import { scenariosByCompanyIdStateJ } from '../../../../services/state/ScenariosStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { ContentContainer } from '../components/commonStyledComponents';
import { filterScenarios } from '../utils/scenarioUtils';
import { CreateScenarioButton } from './CreateScenarioButton';
import { ScenarioList } from './ScenarioList';
import { WeightedScenarios } from './WeightedScenarios';

const ActionContainerCss = css`
  display: grid;
  grid-template-columns: minmax(auto, 24rem) minmax(11rem, auto) 1fr;
  align-items: center;
  column-gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const AllScenarios: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const scenarios = useAtomValue(scenariosByCompanyIdStateJ(companyId)) ?? [];
  const canEditScenarios = PermissionService.get().hasPermission(PermissionKey.canEditScenarios);
  const options = [...new Set(scenarios.map((sc) => sc.createdBy!))];
  const [ownerFilter, setOwnerFilter] = useState<string[] | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const filteredScenarios = filterScenarios(scenarios, searchFilter, ownerFilter);
  const { showWeightedScenarios } = useFlags<MaggieFeatureFlags>();

  return (
    <Typography component='div' color='text.primary'>
      <ContentContainer>
        <div css={ActionContainerCss}>
          <SearchInput
            onChange={setSearchFilter}
            style={{ margin: 0 }}
            placeholder='Search for a scenario name'
          />
          <OwnerFilter options={options} value={ownerFilter ?? []} onChange={setOwnerFilter} />
          {canEditScenarios && <CreateScenarioButton />}
        </div>
        {scenarios.length > 0 &&
          (showWeightedScenarios ? (
            <WeightedScenarios
              companyId={companyId}
              scenarios={scenarios}
              filteredScenarios={filteredScenarios}
              canEdit={canEditScenarios}
            />
          ) : (
            <ScenarioList scenarios={filteredScenarios} />
          ))}
      </ContentContainer>
    </Typography>
  );
};

interface IOwnerFilterProps {
  options: string[];
  value: string[];
  onChange: (newValue: string[]) => void;
}
export const OwnerFilter: FC<IOwnerFilterProps> = ({ options, value, onChange }) => {
  const usersByEmail = useAtomValue(usersByEmailMapAtom);

  return (
    <MultiSelect
      openOnFocus
      options={options}
      value={value}
      renderInput={(params) => {
        return <TextField {...params} placeholder='Filter by owner' sx={{ margin: 0 }} />;
      }}
      getOptionLabel={(option) => (usersByEmail.get(option as string) ?? UnknownUser).name}
      onChange={(e, newValue) => onChange(newValue as string[])}
    />
  );
};
